(() => {
  if (customElements.get('gripz-confidence')) return;

  class GripzConfidence extends HTMLElement {
    static get observedAttributes() {
      return [
        'data-dispatch-enabled', 'data-cutoff-hour', 'data-cutoff-minute', 'data-cutoff-second',
        'data-time-zone', 'data-dispatch-days', 'data-available',
        'data-before-text', 'data-after-text', 'data-closed-text', 'data-closed-day-text',
        'data-shipping-threshold-cents', 'data-shipping-currency',
        'data-cart-total-cents', 'data-cart-currency',
        'data-shipping-remaining', 'data-shipping-unlocked'
      ];
    }

    static createSchedule(data) {
      const hour = Number(data.cutoffHour ?? 17);
      const minute = Number(data.cutoffMinute ?? 0);
      const second = Number(data.cutoffSecond ?? 0);
      const dayTokens = (data.dispatchDays?.trim() || '1,2,3,4,5').split(',').map(day => day.trim());
      if (!Number.isInteger(hour) || hour < 0 || hour > 23 ||
          !Number.isInteger(minute) || minute < 0 || minute > 59 ||
          !Number.isInteger(second) || second < 0 || second > 59 ||
          !dayTokens.every(day => /^[0-6]$/.test(day)) || !data.timeZone?.trim()) return null;
      try {
        return {
          hour, minute, second, days: dayTokens.map(Number),
          formatter: new Intl.DateTimeFormat('en-GB-u-ca-gregory-nu-latn', {
            timeZone: data.timeZone.trim(), year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
          })
        };
      } catch (_) {
        return null;
      }
    }

    static parts(schedule, timestamp) {
      return Object.fromEntries(schedule.formatter.formatToParts(timestamp)
        .filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
    }

    static localTimestamp(parts) {
      return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour || 0, parts.minute || 0, parts.second || 0);
    }

    static stateAt(schedule, timestamp) {
      if (!schedule || !Number.isFinite(timestamp)) return { status: 'invalid' };
      const date = this.parts(schedule, timestamp);
      const key = `${date.year}-${date.month}-${date.day}`;
      if (schedule.dateKey !== key) {
        const target = this.localTimestamp({ ...date, hour: schedule.hour, minute: schedule.minute, second: schedule.second });
        const offsets = new Set();
        // Test both sides of a daylight-saving transition. An ambiguous cutoff uses
        // its first occurrence; a nonexistent local cutoff never promises dispatch.
        for (let hours = -36; hours <= 36; hours += 6) {
          const sample = target + hours * 3600000;
          offsets.add(this.localTimestamp(this.parts(schedule, sample)) - sample);
        }
        const cutoffs = [...offsets].map(offset => target - offset)
          .filter(candidate => this.localTimestamp(this.parts(schedule, candidate)) === target);
        schedule.cutoff = cutoffs.length ? Math.min(...cutoffs) : null;
        schedule.openDay = schedule.days.includes(new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay());
        schedule.dateKey = key;
      }
      if (!schedule.openDay) return { status: 'closed-day' };
      if (schedule.cutoff === null) return { status: 'invalid' };
      if (timestamp >= schedule.cutoff) return { status: 'closed' };
      return { status: 'counting', seconds: Math.ceil((schedule.cutoff - timestamp) / 1000) };
    }

    connectedCallback() {
      this.cleanup();
      this.connected = true;
      this.inViewport = true;
      this.cartRevision = 0;
      this.readCartAttributes();
      this.configureSchedule();
      this.onVisibility = () => this.update();
      this.onCartChange = event => {
        if (this.applyCart(event.detail?.cart)) {
          this.cartRevision += 1;
          clearTimeout(this.cartTimeout);
          this.cartController?.abort();
        } else {
          this.queueCartRefresh();
        }
      };
      this.onCartReload = () => this.queueCartRefresh();
      this.onSectionUpdate = () => this.update();
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('theme:cart:change', this.onCartChange);
      document.addEventListener('theme:cart:reload', this.onCartReload);
      // Variant HTML can update only text/hidden attributes, which does not
      // trigger the element-replacement observer below. Repair after the morph.
      window.addEventListener('theme:section:update', this.onSectionUpdate);

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.inViewport = entries[0]?.isIntersecting !== false;
          this.update();
        });
        this.intersectionObserver.observe(this);
      }
      // Alpine may retain the custom element while replacing its inner markup.
      // Re-query after element replacement; text-only timer updates are ignored.
      this.markupObserver = new MutationObserver(records => {
        if (!records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1))) return;
        if (this.updateQueued) return;
        this.updateQueued = true;
        queueMicrotask(() => {
          this.updateQueued = false;
          if (this.connected) this.update();
        });
      });
      this.markupObserver.observe(this, { childList: true, subtree: true });
      this.update();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      this.connected = false;
      clearTimeout(this.clockTimeout);
      clearTimeout(this.cartTimeout);
      this.cartController?.abort();
      this.intersectionObserver?.disconnect();
      this.markupObserver?.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onCartChange) document.removeEventListener('theme:cart:change', this.onCartChange);
      if (this.onCartReload) document.removeEventListener('theme:cart:reload', this.onCartReload);
      if (this.onSectionUpdate) window.removeEventListener('theme:section:update', this.onSectionUpdate);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.connected || oldValue === newValue) return;
      if (['data-cutoff-hour', 'data-cutoff-minute', 'data-cutoff-second', 'data-time-zone', 'data-dispatch-days'].includes(name)) this.configureSchedule();
      if (name === 'data-cart-total-cents' || name === 'data-cart-currency') this.readCartAttributes();
      this.update();
    }

    configureSchedule() {
      this.schedule = GripzConfidence.createSchedule(this.dataset);
    }

    readCartAttributes() {
      const amount = this.dataset.cartTotalCents;
      const currency = (this.dataset.cartCurrency || '').toUpperCase();
      // Theme variant responses are prefetched/cached. A confirmed cart event
      // or cart.js response is newer than those same-currency HTML attributes.
      if (this.hasConfirmedCart && currency === this.cartCurrency) return;
      this.cartTotal = amount?.trim() ? Number(amount) : NaN;
      this.cartCurrency = currency;
      this.hasConfirmedCart = false;
    }

    setText(element, value) {
      if (element && element.textContent !== value) element.textContent = value;
    }

    update() {
      clearTimeout(this.clockTimeout);
      if (!this.connected || !this.isConnected) return;
      const now = Date.now();
      this.renderDispatch(now);
      this.renderShipping();
      if (this.dataset.dispatchEnabled === 'true' && this.dataset.available === 'true' &&
          this.schedule && this.inViewport && !document.hidden) {
        this.clockTimeout = setTimeout(() => this.update(), 1000 - now % 1000);
      }
    }

    renderDispatch(now) {
      const row = this.querySelector('[data-grz-dispatch]');
      const timer = row?.querySelector('[data-grz-timer]');
      if (!row || !timer) return;
      const state = GripzConfidence.stateAt(this.schedule, now);
      const enabled = this.dataset.dispatchEnabled === 'true' && this.dataset.available === 'true';
      const closedText = state.status === 'closed-day' ?
        (this.dataset.closedDayText ?? 'Dispatch resumes on our next shipping day.') :
        (this.dataset.closedText ?? 'Today’s dispatch cutoff has passed.');
      row.hidden = !enabled || state.status === 'invalid' || (state.status !== 'counting' && !closedText.trim());
      if (row.hidden) return;

      const copy = row.querySelector('[data-grz-dispatch-copy]') || timer.parentElement;
      let before = row.querySelector('[data-grz-before]');
      let after = row.querySelector('[data-grz-after]');
      let closed = row.querySelector('[data-grz-dispatch-closed]');
      if (!before) {
        before = document.createElement('span');
        before.setAttribute('data-grz-before', '');
        timer.before(before);
      }
      if (!after) {
        after = document.createElement('span');
        after.setAttribute('data-grz-after', '');
        timer.after(after);
      }
      if (!closed) {
        closed = document.createElement('span');
        closed.setAttribute('data-grz-dispatch-closed', '');
        copy.append(closed);
      }
      const counting = state.status === 'counting';
      before.hidden = after.hidden = timer.hidden = !counting;
      closed.hidden = counting;
      if (counting) {
        const hours = Math.floor(state.seconds / 3600);
        const minutes = Math.floor(state.seconds % 3600 / 60);
        const seconds = state.seconds % 60;
        const beforeText = this.dataset.beforeText ?? 'Order within';
        const afterText = this.dataset.afterText ?? 'for dispatch today';
        this.setText(before, beforeText ? `${beforeText} ` : '');
        this.setText(after, afterText ? ` ${afterText}` : '');
        this.setText(timer, `${hours}h ${minutes}m ${seconds}s`);
        timer.style.fontVariantNumeric = 'tabular-nums';
      } else {
        this.setText(closed, closedText);
      }
    }

    renderShipping() {
      const row = this.querySelector('[data-grz-shipping-row]');
      const message = this.querySelector('[data-grz-shipping-message]');
      const progress = this.querySelector('[data-grz-shipping-progress]');
      const rawThreshold = this.dataset.shippingThresholdCents;
      const threshold = rawThreshold?.trim() ? Number(rawThreshold) : NaN;
      const currency = (this.dataset.shippingCurrency || '').toUpperCase();
      const valid = Number.isFinite(threshold) && threshold >= 0 && Number.isFinite(this.cartTotal) &&
        this.cartTotal >= 0 && /^[A-Z]{3}$/.test(currency) && currency === this.cartCurrency;
      if (row) row.hidden = !valid;
      if (progress) progress.hidden = !valid;
      if (!valid) return;
      const remaining = Math.max(0, threshold - this.cartTotal);
      const percent = threshold === 0 ? 100 : Math.min(100, this.cartTotal / threshold * 100);
      let amount;
      try {
        const locale = document.documentElement.lang || navigator.language || 'en-US';
        const formatKey = `${locale}:${currency}`;
        if (this.moneyFormatKey !== formatKey) {
          this.moneyFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
          this.moneyFormatKey = formatKey;
        }
        amount = this.moneyFormatter.format(remaining / 100);
      } catch (_) {
        amount = `${currency} ${(remaining / 100).toFixed(2)}`;
      }
      const text = remaining === 0 ? (this.dataset.shippingUnlocked ?? 'Free U.S. shipping unlocked') :
        (this.dataset.shippingRemaining ?? 'You are [amount] away from free U.S. shipping').split('[amount]').join(amount);
      this.setText(message, text);
      if (progress) {
        progress.style.setProperty('--grz-shipping-percent', `${percent}%`);
        if (progress.tagName === 'PROGRESS') {
          progress.max = 100;
          progress.value = percent;
        } else {
          progress.setAttribute('role', 'progressbar');
          progress.setAttribute('aria-valuemin', '0');
          progress.setAttribute('aria-valuemax', '100');
          progress.setAttribute('aria-valuenow', String(Math.round(percent)));
        }
        progress.setAttribute('aria-valuetext', text);
      }
    }

    applyCart(cart) {
      const total = cart?.total_price;
      const currency = typeof cart?.currency === 'string' ? cart.currency.toUpperCase() : '';
      if (typeof total !== 'number' || !Number.isFinite(total) || total < 0 || !/^[A-Z]{3}$/.test(currency || '')) return false;
      this.cartTotal = total;
      this.cartCurrency = currency;
      this.hasConfirmedCart = true;
      this.renderShipping();
      return true;
    }

    queueCartRefresh() {
      clearTimeout(this.cartTimeout);
      const revision = this.cartRevision;
      this.cartTimeout = setTimeout(() => {
        if (this.connected && revision === this.cartRevision) this.refreshCart();
      }, 100);
    }

    async refreshCart() {
      this.cartController?.abort();
      const controller = new AbortController();
      this.cartController = controller;
      const revision = this.cartRevision;
      try {
        const route = window.theme?.routes?.cart || `${window.Shopify?.routes?.root || '/'}cart`;
        const url = new URL(route, window.location.origin);
        if (url.origin !== window.location.origin) return;
        url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
        const response = await fetch(url.href, {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.connected && !controller.signal.aborted && revision === this.cartRevision) this.applyCart(cart);
      } catch (_) {
        // Keep the last confirmed cart total when a refresh cannot be completed.
      }
    }
  }

  customElements.define('gripz-confidence', GripzConfidence);
})();
